import { useEffect, useState } from 'react'
import type { ReviewPhoto, Testimonial } from './testimonials'

const DEFAULT_INTERVAL_MS = 6000

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function Stars() {
  return (
    <span className="text-sage flex gap-px" role="img" aria-label="5 out of 5 stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
          <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z" />
        </svg>
      ))}
    </span>
  )
}

interface HeroReviewTickerProps {
  testimonials: Testimonial[]
  /** Where "See their prints" scrolls to. */
  photosHref: string
  intervalMs?: number
}

/**
 * A one-line review pill under the hero CTAs that rotates through short
 * customer quotes. Auto-advances unless the visitor prefers reduced motion,
 * and pauses while hovered or focused; the dots swap quotes by hand. The
 * live region only announces changes the visitor asked for, never the timer.
 */
export function HeroReviewTicker({
  testimonials,
  photosHref,
  intervalMs = DEFAULT_INTERVAL_MS,
}: HeroReviewTickerProps) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [userChose, setUserChose] = useState(false)
  const count = testimonials.length

  useEffect(() => {
    if (paused || count < 2 || prefersReducedMotion()) return
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), intervalMs)
    return () => window.clearInterval(id)
  }, [paused, count, intervalMs])

  if (count === 0) return null
  const current = testimonials[index]

  // One thumbnail per distinct photo; the current reviewer's sits on top.
  const photos = testimonials.reduce<ReviewPhoto[]>(
    (acc, t) => (!t.photo || acc.some((p) => p.src === t.photo?.src) ? acc : [...acc, t.photo]),
    [],
  )

  return (
    // Hover/focus only pause the rotation (WCAG 2.2.2) — the dots are the real controls.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      aria-label="Customer reviews"
      className="mx-auto mt-4 flex w-full max-w-xl flex-col items-center gap-1 rounded-2xl border border-gray-100 bg-white px-4 py-2.5 text-left shadow-[0_8px_24px_rgba(17,17,17,0.06)] sm:mt-6 sm:flex-row sm:gap-4 sm:rounded-full sm:py-2 sm:pr-6 sm:pl-2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Thumbnails are desktop-only: on a phone the strip must stay short so it
          doesn't squeeze the hero banner above it. */}
      <div className="hidden shrink-0 sm:flex" aria-hidden="true">
        {photos.map((photo, i) => {
          const active = photo.src === current.photo?.src
          return (
            <img
              key={photo.src}
              src={photo.src}
              alt=""
              className={`h-11 w-11 rounded-full border-[3px] border-white object-cover transition-transform duration-300 ${
                i > 0 ? '-ml-3' : ''
              } ${active ? 'relative z-10 scale-110' : 'opacity-80'}`}
            />
          )
        })}
      </div>

      <div className="min-w-0 flex-1 text-center sm:text-left">
        <div className="flex items-center justify-center gap-2 sm:justify-start">
          <Stars />
          <span className="hidden text-xs text-gray-500 sm:inline">
            from makers who’ve printed it
          </span>
        </div>
        <figure
          key={index}
          // Room for a two-line quote, so rotating to a shorter one never shifts the hero.
          className="mt-1 flex min-h-[3.75rem] flex-col justify-center motion-safe:animate-[fadeIn_400ms_ease-out]"
          aria-live={userChose ? 'polite' : 'off'}
        >
          <blockquote className="text-sm font-medium text-black sm:text-[15px]">
            “{current.quote}”
          </blockquote>
          <figcaption className="text-xs text-gray-500">— {current.author}</figcaption>
        </figure>
      </div>

      <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end sm:gap-1">
        <a
          href={photosHref}
          className="text-sage text-sm font-semibold whitespace-nowrap hover:underline"
        >
          See their prints →
        </a>
        {count > 1 && (
          <div className="flex">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show review ${i + 1} of ${count}`}
                aria-pressed={i === index}
                onClick={() => {
                  setUserChose(true)
                  setIndex(i)
                }}
                className="flex h-6 w-6 items-center justify-center"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all ${
                    i === index ? 'bg-sage w-4' : 'w-1.5 bg-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
